import { PartnerName } from "components/reviews/PartnerReview";
import { Contractor } from '../types';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Building2, Phone, Mail, MapPin, Sparkles } from 'lucide-react';

interface ContractorListProps {
  contractors: Contractor[];
  selectedContractor: Contractor | string | number | null;
  onSelectContractor: (contractor: Contractor) => void;
  latestContractorIds?: Set<string | number>;
}

export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
};

const formatUSDateTime = (value: any) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export function ContractorList({
  contractors,
  selectedContractor,
  onSelectContractor,
  latestContractorIds = new Set(),
}: ContractorListProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'monthly':
        return 'bg-green-500/10 text-green-700 border-green-200';
      case 'yearly':
        return 'bg-gray-500/10 text-gray-700 border-gray-200';
      case null:
      case undefined:
      case '':
        return 'bg-red-500/10 text-red-700 border-red-200';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-3">
      {/* Contractor List */}
      {contractors.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-gray-500">
          No contractors match the selected filters.
        </div>
      )}
      {contractors.map((contractor: any) => {
        const ContractrType = contractor.ua_profile
          ? JSON.parse(contractor.ua_profile)
          : { label: 'Contractor' };
        const isLatest = latestContractorIds.has(contractor.id);
        const isSubscribed = Boolean(contractor.active_plan);

        return (
          <div
            key={contractor.id}
            onClick={() => onSelectContractor(contractor)}
            className={`p-2 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
              (typeof selectedContractor === 'object'
                ? selectedContractor?.id
                : selectedContractor) === contractor.id
                ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-2">
              {contractor?.ua_profile_pic ? (
                <img
                  className="h-9 w-9 rounded-full"
                  src={contractor.ua_profile_pic}
                  alt={`${contractor.user_first_name || 'Contractor'} profile`}
                />
              ) : (
                <Avatar className="h-9 w-9 ">
                  <AvatarFallback className="bg-blue-600 text-white">
                    {getInitials(contractor.user_first_name)}
                  </AvatarFallback>
                </Avatar>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="text-gray-900"><PartnerName id={contractor.id}>{contractor.user_first_name +" "+ contractor.user_last_name}</PartnerName></div>
                    <div className="flex items-center gap-2 mt-1 px-2 py-1 bg-blue-50 rounded-md border border-blue-200 w-fit">
                      <Building2 className="h-3.5 w-3.5 text-blue-600" />
                      <div className="text-blue-700">{contractor.company_name}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {isLatest && (
                      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
                        <Sparkles className="mr-1 h-3 w-3" />
                        Latest
                      </Badge>
                    )}
                    <Badge variant="outline" className={getStatusColor(contractor.active_plan)}>
                      {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-gray-600 mb-2">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    <span>{contractor.user_email}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    <span>{contractor.user_phone}</span>
                  </div>
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                      City: <span className="text-gray-900">{contractor.ua_city || contractor.city || '—'}</span>
                    </span>
                  </div>
                  <span>
                    State: <span className="text-gray-900">{contractor.ua_state || contractor.state || '—'}</span>
                  </span>
                  <span>
                    ZIP: <span className="text-gray-900">{contractor.ua_zip_code || contractor.zip_code || contractor.zipCode || '—'}</span>
                  </span>
                </div>
<div className='flex justify-between'>
<div className="flex items-center gap-4">
                  <div className="text-gray-600">
                    <span className="text-gray-900">{contractor.total_projects}</span> projects
                  </div>
                  <div className="text-gray-600">{ContractrType.label}</div>
                </div>
                <div>
                  {formatUSDateTime(contractor.created_at)}
                </div>
</div>
             
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
